import React, { useContext, useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";
import { TranslatorContext } from "../../../context/Translator";
import {
    FiUser,
    FiPhone,
    FiMail,
    FiMapPin,
    FiCalendar,
    FiShoppingBag,
    FiClock,
    FiActivity,
    FiArrowLeft,
    FiRefreshCw,
} from "react-icons/fi";
import { FaPaw, FaMoneyBillWave } from "react-icons/fa";
import CustomerInfoTab from "./tabs/CustomerInfoTab";
import PetsTab from "./tabs/PetsTab";
import SalesTab from "./tabs/SalesTab";

const VISIBLE_TABS = ["customer", "pets", "sales"];

export default function CustomerProfilePage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const location = useLocation();

    const navigationState = location.state || {};
    const searchedPetId = navigationState.petId || null;

    // Callers elsewhere in the app ask for a tab by name, so a name this page no
    // longer has falls back to the profile rather than an empty panel.
    const requestedTab = VISIBLE_TABS.includes(navigationState.activeTab)
        ? navigationState.activeTab
        : "customer";

    const [customer, setCustomer] = useState(null);
    const [summary, setSummary] = useState(null);
    const [pets, setPets] = useState([]);
    const [breeds, setBreeds] = useState([]);
    const [totalPets, setTotalPets] = useState(0);
    const [showAllPets, setShowAllPets] = useState(false);
    const [sales, setSales] = useState([]);
    const [salesSummary, setSalesSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingPets, setLoadingPets] = useState(false);
    const [loadingSales, setLoadingSales] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeTab, setActiveTab] = useState(requestedTab);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // A pet search asks for one pet, so only that pet is requested until the
    // user chooses to see the rest of the household.
    const petFilterId = showAllPets ? null : searchedPetId;

    useEffect(() => {
        fetchCustomerDetails();
    }, [id]);

    useEffect(() => {
        setActiveTab(requestedTab);
    }, [id, requestedTab]);

    useEffect(() => {
        setShowAllPets(false);
    }, [id, searchedPetId]);

    useEffect(() => {
        if (activeTab === "pets") {
            fetchCustomerPets();
        } else if (activeTab === "sales") {
            fetchSales();
        }
    }, [activeTab, id, refreshTrigger, petFilterId]);

    const authHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    });

    const fetchCustomerDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers-profile/${id}`,
                authHeaders()
            );

            if (response.data.customer) {
                setCustomer(response.data.customer);
                setSummary(response.data.summary || null);
                setError(null);
            }
        } catch (err) {
            console.error("Error fetching customer:", err);
            setError("Failed to load customer details");
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerPets = async () => {
        try {
            setLoadingPets(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers-pets/${id}`,
                {
                    ...authHeaders(),
                    params: petFilterId ? { pet_id: petFilterId } : {},
                }
            );

            if (response.data.success) {
                setPets(response.data.pets || []);
                setBreeds(response.data.breeds || []);
                setTotalPets(response.data.total_pets ?? (response.data.pets || []).length);
                setError(null);
            }
        } catch (err) {
            console.error("Error fetching pets:", err);
            setError("Failed to load pets");
        } finally {
            setLoadingPets(false);
        }
    };

    const fetchSales = async () => {
        setLoadingSales(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers-sales/${id}`,
                authHeaders()
            );

            if (response.data.status) {
                setSales(response.data.sales || []);
                setSalesSummary(response.data.summary || null);
            }
        } catch (err) {
            console.error("Error fetching sales:", err);
        } finally {
            setLoadingSales(false);
        }
    };

    const refreshSalesData = async () => {
        await fetchSales();
        await fetchCustomerDetails();
    };

    const refreshPets = () => {
        setRefreshTrigger((prev) => prev + 1);
    };

    const showSuccess = (message) => {
        setSuccess(message);
        setTimeout(() => setSuccess(null), 3000);
    };

    const showError = (message) => {
        setError(message);
        setTimeout(() => setError(null), 3000);
    };

    const formatDate = (value) => {
        if (!value) return "-";

        return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }).format(new Date(value));
    };

    const formatAmount = (value) =>
        value === null || value === undefined
            ? "-"
            : parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 });

    const initials = (name) =>
        (name || "?")
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0].toUpperCase())
            .join("");

    const imageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http")) return path;
        return `${process.env.REACT_APP_IMG_URL}${path}`;
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="cp-empty">
                    <Spinner animation="border" variant="primary" />
                    <p>{t("loading")}</p>
                </div>
            </PageLayout>
        );
    }

    if (!customer) {
        return (
            <PageLayout>
                <Alert variant={error ? "danger" : "warning"} className="m-4">
                    {error || t("Customer not found")}
                </Alert>
            </PageLayout>
        );
    }

    const outstanding = summary?.outstanding_balance ?? 0;
    const hasDue = outstanding > 0;

    const stats = [
        {
            tone: "is-blue",
            icon: <FaPaw size={17} />,
            label: t("pets"),
            value: summary?.pets_count ?? 0,
        },
        {
            tone: "is-teal",
            icon: <FiCalendar size={17} />,
            label: t("appointments"),
            value: summary?.appointments_count ?? 0,
        },
        {
            tone: "is-violet",
            icon: <FiShoppingBag size={17} />,
            label: t("sales"),
            value: summary?.sales_count ?? 0,
        },
        {
            tone: "is-rose",
            icon: <FiActivity size={17} />,
            label: t("Total Billed"),
            value: formatAmount(summary?.total_billed ?? 0),
        },
        {
            tone: "is-teal",
            icon: <FaMoneyBillWave size={16} />,
            label: t("Total Paid"),
            value: formatAmount(summary?.total_paid ?? 0),
        },
        {
            tone: "is-amber",
            icon: <FiClock size={17} />,
            label: t("Last Visit"),
            value: formatDate(summary?.last_appointment_date),
        },
    ];

    const tabs = [
        { key: "customer", label: t("customer information"), icon: <FiUser size={15} /> },
        {
            key: "pets",
            label: t("pets"),
            icon: <FaPaw size={14} />,
            count: summary?.pets_count,
        },
        {
            key: "sales",
            label: t("sales"),
            icon: <FiShoppingBag size={15} />,
            count: summary?.sales_count,
        },
    ];

    return (
        <PageLayout>
            <div className="cp-page">
                <section className="cp-hero">
                    <div className="cp-hero-inner">
                        <div className="cp-identity">
                            <div className="cp-avatar">
                                {customer.img ? (
                                    <img src={imageUrl(customer.img)} alt={customer.name} />
                                ) : (
                                    initials(customer.name)
                                )}
                            </div>

                            <div className="cp-identity-body">
                                <div className="cp-name-row">
                                    <h3 className="cp-name">{customer.name || t("Unnamed customer")}</h3>
                                    <span className="cp-pill">#{customer.id}</span>
                                    {navigationState.source === "pet-search" && navigationState.petName && (
                                        <span className="cp-pill is-accent">
                                            <FaPaw size={10} /> {navigationState.petName}
                                        </span>
                                    )}
                                </div>

                                <div className="cp-meta">
                                    <span className="cp-meta-item">
                                        <FiPhone size={13} /> {customer.phone || "-"}
                                    </span>
                                    <span className="cp-meta-item">
                                        <FiMail size={13} /> {customer.email || "-"}
                                    </span>
                                    <span className="cp-meta-item">
                                        <FiMapPin size={13} /> {customer.address || "-"}
                                    </span>
                                    <span className="cp-meta-item">
                                        <FiUser size={13} /> {t("Customer since")}{" "}
                                        {formatDate(summary?.customer_since)}
                                    </span>
                                </div>

                                <div className="cp-meta">
                                    <Link to="/customers" className="cp-meta-item">
                                        <FiArrowLeft size={13} /> {t("customers")}
                                    </Link>
                                    <button
                                        type="button"
                                        className="cp-meta-item"
                                        onClick={() => {
                                            fetchCustomerDetails();
                                            setRefreshTrigger((prev) => prev + 1);
                                        }}
                                    >
                                        <FiRefreshCw size={13} /> {t("Refresh")}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={`cp-balance ${hasDue ? "is-due" : "is-clear"}`}>
                            <span className="cp-balance-label">{t("Outstanding")}</span>
                            <span className="cp-balance-value">{formatAmount(outstanding)}</span>
                            <span className="cp-balance-note">
                                {hasDue
                                    ? `${t("Sales due")} ${formatAmount(summary?.sales_due ?? 0)} · ${t(
                                          "Appointments due"
                                      )} ${formatAmount(summary?.appointments_due ?? 0)}`
                                    : t("No pending balance")}
                            </span>
                        </div>
                    </div>
                </section>

                <div className="cp-stats">
                    {stats.map((stat, index) => (
                        <div key={index} className={`cp-stat ${stat.tone}`}>
                            <div className="cp-stat-icon">{stat.icon}</div>
                            <div className="cp-stat-body">
                                <span className="cp-stat-label">{stat.label}</span>
                                <span className="cp-stat-value">{stat.value}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {success && (
                    <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-0">
                        {success}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-0">
                        {error}
                    </Alert>
                )}

                <div className="cp-surface">
                    <nav className="cp-tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                className={`cp-tab ${activeTab === tab.key ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.icon}
                                {tab.label}
                                {tab.count > 0 && <span className="cp-tab-count">{tab.count}</span>}
                            </button>
                        ))}
                    </nav>

                    <div className="cp-panel">
                        {activeTab === "customer" && (
                            <CustomerInfoTab
                                customer={customer}
                                customerId={id}
                                onCustomerUpdate={fetchCustomerDetails}
                                onShowSuccess={showSuccess}
                                onShowError={showError}
                            />
                        )}

                        {activeTab === "pets" && (
                            <PetsTab
                                pets={pets}
                                breeds={breeds}
                                customerId={id}
                                loading={loadingPets}
                                highlightPetId={searchedPetId}
                                totalPets={totalPets}
                                isFilteredToPet={Boolean(petFilterId)}
                                onShowAllPets={() => setShowAllPets(true)}
                                onPetsUpdate={refreshPets}
                                onShowSuccess={showSuccess}
                                onShowError={showError}
                            />
                        )}

                        {activeTab === "sales" && (
                            <SalesTab
                                sales={sales}
                                summary={salesSummary}
                                loading={loadingSales}
                                customerId={id}
                                onRefresh={refreshSalesData}
                            />
                        )}
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}
